import {useEffect} from 'react';
import usePayinSessionQuery from 'src/api/graphql/usePayinSessionQuery';
import useSetupSessionQuery from 'src/api/graphql/useSetupSessionQuery';

import type {SessionContext} from 'src/state/usePayinSessionContext';
import {useLogger} from 'src/utils/logger';
import {getStore} from 'src/v2/state/store';
import {
  convertGraphQlToFeatureFlags,
  convertGraphQlToWalletSession,
  convertGraphQlToPaymentDetails,
  convertGraphQlToTransactionDetails,
  convertGraphQlToExternalPaymentDetails,
  convertGraphQlToSupportedCurrency,
  convertGraphQlToMerchantBranding,
  convertGraphQlToMandateDetails,
} from 'src/v2/util/convertGraphQlToStoreState';
import type {SettlementPartner} from 'src/v2/state/types';
import type {CryptoPayinSession, CryptoSetupSession} from 'src/types';

let renderLoadedCount = 0;

// Fetches the setup/payin session model after the app first loads when we have the client secret from the slug
export const useStateInitialize = (sessionContext: SessionContext) => {
  const data = sessionContext;
  const store = getStore();
  const logger = useLogger();

  if (data && 'error' in data) {
    throw data.error;
  }

  const mode = data?.mode;

  const payinSessionData = usePayinSessionQuery({
    variables: {
      input: {
        crypto_payin_session: data.sessionId,
        client_secret: data.clientSecret,
      },
      v1Context: {apiKey: data.publishableKey},
    },
    skip: !data || mode !== 'pay',
    component: 'useStateInitialize',
  });

  const setupSessionData = useSetupSessionQuery({
    variables: {
      input: {
        crypto_setup_session: data.sessionId,
        client_secret: data.clientSecret,
      },
    },
    skip: !data || mode !== 'setup',
    component: 'useStateInitialize',
  });

  const alreadySetModel = store.useSelector((state) => {
    return state.id.length > 0;
  });

  useEffect(() => {
    //    We only want to set the session when there's no session in the store
    if (data && !alreadySetModel) {
      let session: CryptoPayinSession | CryptoSetupSession | null;
      if (mode === 'pay') {
        session = payinSessionData?.data?.v1CryptoInternalPayinSession || null;
      } else {
        session = setupSessionData?.data?.v1CryptoInternalSetupSession || null;
      }

      if (session) {
        const isPayinSession =
          session.__typename ===
          'OpenapiCryptocurrencyPayinsResourceCryptoPayinSession';

        // setup sessions will always have bridge as the settlement partner
        const settlementPartner = isPayinSession
          ? ((session as CryptoPayinSession)
              .settlement_partner as SettlementPartner)
          : 'bridge';

        store.setValue((state) => {
          state.id = session.id;
          state.livemode = session.livemode;
          state.merchant = session.merchant;
          state.consumerAccount = session.consumer_account;
          state.consumerWallet = session.consumer_wallet;
          state.state = session.state;
          state.settlementPartner = settlementPartner;
          state.clientSecret = session.client_secret;
          state.setupFutureUsage = isPayinSession
            ? (session as CryptoPayinSession).setup_future_usage
            : true;
          state.hasSucceededOrProcessingSetupAttempt = isPayinSession
            ? (session as CryptoPayinSession)
                .has_succeeded_or_processing_setup_attempt
            : false;
          state.featureFlags = convertGraphQlToFeatureFlags(
            session.feature_flags,
          );
          state.supportedCurrencies = session.supported_currencies.map((c) => ({
            ...convertGraphQlToSupportedCurrency(c),
            paymentOptions: [...(c.payment_options ?? [])],
          }));
          state.walletConnectSupportedCurrencies = isPayinSession
            ? state.supportedCurrencies.filter((c) =>
                c.paymentOptions.includes('wallet_connect'),
              )
            : state.supportedCurrencies;
          state.directDepositSupportedCurrencies =
            state.supportedCurrencies.filter((c) =>
              c.paymentOptions.includes('direct_deposit'),
            );
          state.selectedCurrency = null;
          state.walletSessions = session.wallet_sessions.map(
            convertGraphQlToWalletSession,
          );
          state.depositAddresses =
            'deposit_addresses_do_not_fund_directly' in session
              ? session.deposit_addresses_do_not_fund_directly
              : {};
          state.spenderContractAddresses =
            session.spender_contract_addresses || {};
          state.validBefore = session.valid_before;
          // Assign only when the session actually has the field. Falling back to
          // `state.x` here would read the field back through the state manager's
          // proxy, which collapses `null` to `undefined` on read - the resulting
          // reassignment then gets treated as a replacement and permanently loses
          // any `null`-valued fields (e.g. paymentDetails.originalAmount).
          if ('payment_details' in session) {
            state.paymentDetails = convertGraphQlToPaymentDetails(
              session.payment_details,
            );
          }
          if ('transaction_details' in session) {
            state.transactionDetails = convertGraphQlToTransactionDetails(
              session.transaction_details,
            );
          }
          if ('external_payment_details' in session) {
            state.externalPaymentDetails =
              convertGraphQlToExternalPaymentDetails(
                session.external_payment_details,
              );
          }
          if ('payment_mandate' in session) {
            state.mandateDetails = convertGraphQlToMandateDetails(
              session.payment_mandate,
            );
          }
          state.merchantBranding = convertGraphQlToMerchantBranding(
            session.merchant_branding?.background_color || null,
            session.merchant_branding?.button_color || null,
            session.business_name,
            session.logo_url,
            session.consumer_account,
          );
          state.disabledWalletProviders = [
            ...(session.disabled_wallet_providers ?? []),
          ];
          if (
            isPayinSession &&
            'customer_email' in session &&
            typeof session.customer_email === 'string'
          ) {
            state.anyCryptoDeposit.customerEmail = session.customer_email;
          }
        });

        logger.updateBaseParams({
          payin_session_id: session.id,
          mode: session.livemode.toString(),
        });

        renderLoadedCount += 1;
        const navigationType =
          (
            performance.getEntriesByType('navigation')?.[0] as
              | PerformanceNavigationTiming
              | undefined
          )?.type ?? 'unknown';

        // navigation_type distinguishes browser reload vs fresh navigation.
        // render_loaded_count is a module-level counter that resets on full page
        // reload (new JS context) but increments on in-app re-renders, helping
        // identify if Coinbase's WebView is causing a real browser reload.
        logger.log('crypto_payin_session.render.loaded', {
          navigation_type: navigationType,
          render_loaded_count: renderLoadedCount,
        });
      }
    }
  }, [
    data,
    mode,
    logger,
    store,
    payinSessionData,
    alreadySetModel,
    setupSessionData,
  ]);
};
